import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme modes
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};

const THEME_STORAGE_KEY = 'lms_theme_mode';

// Theme context
const ThemeContext = createContext(null);

// Get system preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME_MODES.DARK : THEME_MODES.LIGHT;
  }
  return THEME_MODES.LIGHT;
};

// Get stored theme mode
const getStoredThemeMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && Object.values(THEME_MODES).includes(stored)) {
      return stored;
    }
  } catch (e) {
    console.error('Error reading theme from storage:', e);
  }
  return THEME_MODES.LIGHT;
};

// Resolve actual theme from mode
const resolveTheme = (mode) => {
  if (mode === THEME_MODES.AUTO) {
    return getSystemTheme();
  }
  return mode;
};

// Theme Provider component
export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(getStoredThemeMode);
  const [actualTheme, setActualTheme] = useState(() => resolveTheme(getStoredThemeMode()));

  // Set theme mode
  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setActualTheme(resolveTheme(mode));
  }, []);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (themeMode !== THEME_MODES.AUTO) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setActualTheme(e.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', actualTheme);

    // Toggle dark mode class on body
    if (actualTheme === THEME_MODES.DARK) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Repaint the PWA status bar to match the page ground. Values mirror
    // --color-bg-page in index.css; index.html sets the same before first paint.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', actualTheme === THEME_MODES.DARK ? '#14120f' : '#faf7f1');
    }
  }, [actualTheme]);

  const value = {
    themeMode,
    actualTheme,
    setThemeMode,
    isDark: actualTheme === THEME_MODES.DARK
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default useTheme;

